import { useParams, useRouter, useNavigate, createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { PageContainer } from "@/components/shared/page-container";
import { Button } from "@/components/ui/button";

// API hooks
import {
  useCompetitionGame,
  useGameHistory,
  useHasCompletedGames,
  useCompetitionScoresByGroup,
} from "@/lib/api/competition/hooks";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { useStudents } from "@/lib/api/students/hooks";
import { useCompetitionSocket } from "@/lib/hooks/use-competition-socket";
import { useGetProfile } from "@/lib/api/auth/hooks";
import { useWordsByPages } from "@/lib/api/vocabulary/hooks";

// FSD imports
import type { PendingSelection } from "@/entities/competition";
import {
  toLocalLeagues,
  toLocalPlayers,
  upsertMatch,
  type LocalLeague,
  type LocalPlayer,
} from "@/entities/competition";
import {
  useGameState,
  useGameActions,
  useLateJoin,
  useGameSocketHandlers,
} from "@/features/competition";
import {
  useFlashcardBattle,
  FlashcardBattleOverlay,
  type CategorySelection,
} from "@/features/competition/flashcard-battle";
import { ResultsTable } from "@/widgets/competition";
import {
  GameHeader,
  ValidationWarnings,
  ReadyStateView,
  InProgressStateView,
} from "@/views/competition-demo";
import { DoneEditView } from "@/views/competition-done";

function CompetitionGamePage() {
  const params = useParams({ strict: false });
  const router = useRouter();
  const navigate = useNavigate();
  const gameId = params.gameId as string;

  // 현재 사용자
  const { data: currentUser } = useGetProfile();

  // REST API로 초기 데이터 조회
  const {
    data: initialGame,
    isLoading,
    error: gameError,
    refetch,
  } = useCompetitionGame(gameId);

  // WebSocket 연결 (실시간 동기화)
  const {
    game: socketGame,
    isConnected,
    updateSettings,
    toggleLeagueManage,
    addPlayer: wsAddPlayer,
    startGame: wsStartGame,
    recordMatch: wsRecordMatch,
    finishLeague: wsFinishLeague,
  } = useCompetitionSocket(gameId, {
    onGameUpdated: () => toast.success("게임이 업데이트되었습니다."),
    onGameStarted: () => toast.success("게임이 시작되었습니다!"),
    onMatchRecorded: (data) => {
      // 다른 선생님의 매치 결과를 로컬에 동기화
      const { leagueName, player1, player2, winner, isOvertime } = data;
      if (!winner) return;
      gameState.setLocalLeagues((prev) =>
        prev.map((league) => {
          if (league.name !== leagueName) return league;
          if (isOvertime) {
            return {
              ...league,
              isOvertime: true,
              overtimeMatches: upsertMatch(league.overtimeMatches, player1, player2, winner),
            };
          }
          return {
            ...league,
            matches: upsertMatch(league.matches, player1, player2, winner),
          };
        }),
      );
    },
    onLeagueFinished: (data) => toast.success(`리그 ${data.leagueName} 종료!`),
    onPlayerAdded: (data) => {
      // 추가된 학생 정보 찾기
      const addedPlayer = data.game.leagues
        .find((l) => l.name === data.leagueName)
        ?.players.find((p) => p.user._id === data.userId);

      if (!addedPlayer) return;

      const playerName = addedPlayer.user.name ?? "학생";

      // 리그에 플레이어 추가 (기존 매치 결과 보존)
      gameState.setLocalLeagues((prev) =>
        prev.map((league) => {
          if (league.name === data.leagueName) {
            return { ...league, playerCount: league.playerCount + 1 };
          }
          return league;
        })
      );

      // 플레이어 목록에 추가
      gameState.setLocalPlayers((prev) => [
        ...prev,
        {
          id: addedPlayer.user._id,
          name: addedPlayer.user.name,
          score: addedPlayer.score ?? 0,
          isSaved: true,
          isFromPreviousGame: false,
          isLateJoin: true,
          assignedLeague: data.leagueName,
        },
      ]);

      // saved 상태도 업데이트
      gameState.setSavedLeagues((prev) =>
        prev.map((league) => {
          if (league.name === data.leagueName) {
            return { ...league, playerCount: league.playerCount + 1 };
          }
          return league;
        })
      );

      gameState.setSavedPlayers((prev) => [
        ...prev,
        {
          id: addedPlayer.user._id,
          name: addedPlayer.user.name,
          score: addedPlayer.score ?? 0,
          isSaved: true,
          isFromPreviousGame: false,
          isLateJoin: true,
          assignedLeague: data.leagueName,
        },
      ]);

      toast.success(`${playerName}님이 리그 ${data.leagueName}에 추가되었습니다.`);
    },
    onGameFinished: () => toast.success("게임이 종료되었습니다!"),
    onError: (err) => toast.error(err.message),
  });

  // 실제 사용할 게임 데이터 (WebSocket 우선, 없으면 REST)
  const game = socketGame ?? initialGame;

  // FSD 훅 사용
  const gameState = useGameState();

  // 초기 로드 완료 여부
  const [isInitialized, setIsInitialized] = useState(false);

  // 이전 게임 플레이어 맵
  const [previousGamePlayers, setPreviousGamePlayers] = useState<
    Map<string, { score: number; league: string }>
  >(new Map());

  // Pending selections
  const [pendingSelections, setPendingSelections] = useState<
    PendingSelection[]
  >([]);

  // Done 상태 편집모드
  const [isEditMode, setIsEditMode] = useState(false);

  // 플래시카드 배틀 상태
  const [categorySelection, setCategorySelection] =
    useState<CategorySelection | null>(null);
  const flashcardBattle = useFlashcardBattle();

  // 학생 목록 조회
  const { data: studentsData } = useStudents({
    officeId: game?.office._id,
    limit: 1000,
  });

  // 이전 게임 히스토리 조회
  const { data: historyData } = useGameHistory(
    game?.group._id ?? "",
    gameState.previousGameDate,
  );

  // 그룹에 완료된 게임이 있는지 확인 (독립모드 판단용)
  const { data: hasCompletedGames } = useHasCompletedGames(game?.group._id ?? "");

  // 독립모드 여부 (그룹에 완료된 게임이 없으면 독립모드)
  const isIndependentMode = hasCompletedGames === false;

  // 플래시카드 배틀용 단어 조회
  const { data: battleWords } = useWordsByPages(
    categorySelection?.categoryId ?? "",
    categorySelection?.pages ?? [],
  );

  // 이전 게임 데이터 로드 시 플레이어 맵 업데이트
  useEffect(() => {
    if (!historyData) return;
    const map = new Map<string, { score: number; league: string }>();
    historyData.leagues?.forEach((league) => {
      league.players?.forEach((player) => {
        if (!player.user) return;
        map.set(player.user._id, {
          score: player.score ?? 0,
          league: league.name,
        });
      });
    });
    setPreviousGamePlayers(map);
  }, [historyData]);

  // 게임 생성 시 이전 게임 날짜가 설정되어 있으면 자동으로 데이터 로드
  // (다이얼로그에서 날짜 선택 후 생성한 경우)
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  useEffect(() => {
    // 이미 자동 로드했거나, 초기화 안됐거나, 히스토리 데이터 없으면 스킵
    if (hasAutoLoaded || !isInitialized || !historyData?.leagues) return;

    // 플레이어가 이미 있으면 스킵 (기존 게임 편집 등)
    if (gameState.localPlayers.length > 0) return;

    // 이전 게임 날짜가 없으면 스킵
    if (!gameState.previousGameDate) return;

    // ready 상태가 아니면 스킵
    if (game?.status !== "ready") return;

    // 플레이어 목록 생성
    const players: LocalPlayer[] = [];
    historyData.leagues.forEach((league) => {
      league.players?.forEach((player) => {
        if (!player.user) return;
        players.push({
          id: player.user._id,
          name: player.user.name,
          score: player.score ?? 0,
          isSaved: false,
          isFromPreviousGame: true,
        });
      });
    });

    // 리그 설정 (이전 게임 리그 구조 유지)
    const leagues: LocalLeague[] = historyData.leagues.map((league) => ({
      name: league.name,
      playerCount: league.players?.length ?? 0,
      managedBy: null,
      matches: [],
      isFinished: false,
      isOvertime: false,
      overtimeMatches: [],
    }));

    // 상태 업데이트
    gameState.setLocalPlayers(players);
    gameState.setLocalLeagues(leagues);
    setHasAutoLoaded(true);

    toast.success("지난 게임 데이터를 자동으로 불러왔습니다.");
  }, [isInitialized, historyData, gameState, game?.status, hasAutoLoaded]);

  // 서버 데이터로 FSD 상태 초기화
  useEffect(() => {
    if (!game) return;

    // 초기 로드: 전체 상태 초기화
    if (!isInitialized) {
      const serverLeagues = toLocalLeagues(game);
      const serverPlayers = toLocalPlayers(game);

      gameState.setLocalLeagues(serverLeagues);
      gameState.setLocalPlayers(serverPlayers);
      gameState.setLocalPromotionCount(game.promotionCount.toString());

      gameState.setSavedLeagues(serverLeagues);
      gameState.setSavedPlayers(serverPlayers);
      gameState.setSavedPromotionCount(game.promotionCount);

      if (game.status === "ready") {
        gameState.setGameStatus("ready");
      } else if (game.status === "in_progress") {
        gameState.setGameStatus("in_progress");
      } else if (game.status === "done") {
        gameState.setGameStatus("done");
      }

      // 이전 게임 날짜 초기화
      const prevDate = game.previousGameDate
        ? game.previousGameDate.split("T")[0]
        : "";
      gameState.setPreviousGameDate(prevDate);
      gameState.setConfirmedPreviousGameDate(prevDate);
      gameState.setSavedPreviousGameDate(prevDate);

      setIsInitialized(true);
      return;
    }

    // 독립모드 적용: 그룹의 첫 게임이면 승급 인원 0으로 강제 설정
    if (isIndependentMode && game.status === "ready") {
      if (gameState.localPromotionCount !== "0") {
        gameState.setLocalPromotionCount("0");
        gameState.setSavedPromotionCount(0);
      }
    }

    // 게임 상태 변경 감지 (예: ready → in_progress, in_progress → done)
    // 상태가 변경되면 전체 데이터를 다시 동기화
    if (game.status !== gameState.gameStatus) {
      const serverLeagues = toLocalLeagues(game);
      const serverPlayers = toLocalPlayers(game);

      gameState.setLocalLeagues(serverLeagues);
      gameState.setLocalPlayers(serverPlayers);
      gameState.setSavedLeagues(serverLeagues);
      gameState.setSavedPlayers(serverPlayers);
      gameState.setGameStatus(game.status);
      return;
    }

    // 상태 변경 없음: managedBy, isFinished, matches, overtimeMatches, isOvertime 동기화
    gameState.setLocalLeagues((prev) =>
      prev.map((localLeague) => {
        const serverLeague = game.leagues.find(
          (l) => l.name === localLeague.name,
        );
        if (serverLeague) {
          return {
            ...localLeague,
            managedBy: serverLeague.managedBy
              ? {
                  _id: serverLeague.managedBy._id,
                  name: serverLeague.managedBy.name ?? "",
                }
              : null,
            isFinished: serverLeague.isFinished,
            matches: serverLeague.matches.map((m) => ({
              player1: m.player1,
              player2: m.player2,
              winner: m.winner,
            })),
            isOvertime: serverLeague.isOvertime ?? localLeague.isOvertime,
            overtimeMatches: (serverLeague.overtimeMatches ?? []).map((m) => ({
              player1: m.player1,
              player2: m.player2,
              winner: m.winner,
            })),
          };
        }
        return localLeague;
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, isIndependentMode]);

  // 게임 생성자인지 확인
  const isCreator = useMemo(() => {
    if (!game || !currentUser) return false;
    return game.createdBy._id === currentUser._id;
  }, [game, currentUser]);

  // 그룹별 CompetitionScore 조회 (최근 3게임 평균 계산용)
  const { data: groupScores } = useCompetitionScoresByGroup(game?.group._id ?? "");

  // 학생 평균 점수 맵 (최근 3게임 평균)
  const studentAverageScores = useMemo(() => {
    const map = new Map<string, number>();
    if (!groupScores) return map;

    for (const score of groupScores) {
      if (!score.user) continue;
      const history = score.history;
      if (!history || history.length === 0) continue;

      const recent = history.slice(-3);
      const avg = recent.reduce((sum, h) => sum + h.score, 0) / recent.length;
      map.set(score.user._id, Math.round(avg * 10) / 10);
    }
    return map;
  }, [groupScores]);

  // 현재 그룹 학생 목록
  const currentGroupStudents = useMemo(() => {
    if (!studentsData?.students) return [];
    return studentsData.students.map((s) => ({
      _id: s._id,
      name: s.name,
      averageScore: studentAverageScores.get(s._id) ?? 0,
    }));
  }, [studentsData, studentAverageScores]);

  // 추가 가능한 학생 목록
  const availableStudents = useMemo(() => {
    const addedIds = new Set(gameState.localPlayers.map((p) => p.id));
    const pendingIds = new Set(pendingSelections.map((p) => p.studentId));
    return currentGroupStudents
      .filter((s) => !addedIds.has(s._id) && !pendingIds.has(s._id))
      .map((s) => {
        const previousPlayer = previousGamePlayers.get(s._id);
        if (previousPlayer) {
          return {
            ...s,
            displayScore: previousPlayer.score,
            scoreSource: "지난 게임" as const,
          };
        }
        return {
          ...s,
          displayScore: s.averageScore,
          scoreSource: "평균" as const,
        };
      });
  }, [
    currentGroupStudents,
    gameState.localPlayers,
    pendingSelections,
    previousGamePlayers,
  ]);

  // FSD useGameActions 훅 사용
  const gameActions = useGameActions({
    currentUser: currentUser
      ? {
          _id: currentUser._id,
          name: currentUser.name,
          userId: currentUser.userId,
        }
      : { _id: "", name: "", userId: "" },
    localLeagues: gameState.localLeagues,
    setLocalLeagues: gameState.setLocalLeagues,
    localPlayers: gameState.localPlayers,
    setLocalPlayers: gameState.setLocalPlayers,
    savedLeagues: gameState.savedLeagues,
    setSavedLeagues: gameState.setSavedLeagues,
    savedPlayers: gameState.savedPlayers,
    setSavedPlayers: gameState.setSavedPlayers,
    localPromotionCount: gameState.localPromotionCount,
    setLocalPromotionCount: gameState.setLocalPromotionCount,
    savedPromotionCount: gameState.savedPromotionCount,
    setSavedPromotionCount: gameState.setSavedPromotionCount,
    setPreviousGameDate: gameState.setPreviousGameDate,
    setConfirmedPreviousGameDate: gameState.setConfirmedPreviousGameDate,
    savedPreviousGameDate: gameState.savedPreviousGameDate,
    playersWithLeague: gameState.playersWithLeague,
    setGameStatus: gameState.setGameStatus,
    canSave: gameState.canSave,
    canStart: gameState.canStart,
    canDelete: isCreator && game?.status !== "in_progress",
    startError: gameState.startError,
    isLeagueComplete: gameState.isLeagueComplete,
    isOvertimeComplete: gameState.isOvertimeComplete,
  });

  // Late join 콜백 (in_progress 상태에서는 WebSocket으로 서버에 저장)
  const handleConfirmLateJoin = useCallback(
    (
      studentId: string,
      studentName: string,
      score: number,
      selectedLeague: string,
      isFromPreviousGame: boolean
    ) => {
      if (game?.status === "in_progress") {
        // 서버에 저장 (WebSocket)
        wsAddPlayer({ userId: studentId, leagueName: selectedLeague });
      } else {
        // 로컬 상태만 업데이트 (ready 상태)
        gameActions.handleConfirmLateJoinPlayer(
          studentId,
          studentName,
          score,
          selectedLeague,
          isFromPreviousGame
        );
      }
    },
    [game?.status, wsAddPlayer, gameActions]
  );

  // Late join 훅 사용
  const lateJoin = useLateJoin({
    localPlayers: gameState.localPlayers,
    localLeagues: gameState.localLeagues,
    previousGamePlayers,
    studentAverageScores,
    isIndependentMode,
    onConfirmLateJoin: handleConfirmLateJoin,
  });

  // WebSocket 연동 핸들러 훅
  const socketHandlers = useGameSocketHandlers({
    gameId,
    gameStatus: game?.status,
    isCreator,
    gameState,
    gameActions,
    previousGamePlayers,
    studentAverageScores,
    updateSettings,
    toggleLeagueManage,
    wsStartGame,
    wsRecordMatch,
    wsFinishLeague,
  });

  // 로딩 인디케이터
  useLoadingIndicator(
    isLoading || socketHandlers.isSaving || socketHandlers.isStarting,
  );

  // Pending student 리그 예측
  const getPendingStudentLeague = useCallback(
    (studentId: string): string => {
      const previousPlayer = previousGamePlayers.get(studentId);
      const student = currentGroupStudents.find((s) => s._id === studentId);
      const score = previousPlayer?.score ?? student?.averageScore ?? 0;

      const sortedExisting = [...gameState.localPlayers].sort(
        (a, b) => b.score - a.score,
      );
      let newPlayerIndex = sortedExisting.findIndex((p) => p.score < score);
      if (newPlayerIndex === -1) {
        newPlayerIndex = sortedExisting.length;
      }

      const totalCapacity = gameState.localLeagues.reduce(
        (sum, l) => sum + l.playerCount,
        0,
      );
      if (newPlayerIndex >= totalCapacity) {
        return "-";
      }

      let recommendedLeague =
        gameState.localLeagues[gameState.localLeagues.length - 1]?.name ?? "A";
      let cumulative = 0;
      for (const league of gameState.localLeagues) {
        cumulative += league.playerCount;
        if (newPlayerIndex < cumulative) {
          recommendedLeague = league.name;
          break;
        }
      }
      return recommendedLeague;
    },
    [
      gameState.localPlayers,
      gameState.localLeagues,
      previousGamePlayers,
      currentGroupStudents,
    ],
  );

  // Pending student 점수
  const getPendingStudentScore = useCallback(
    (studentId: string): number => {
      const previousPlayer = previousGamePlayers.get(studentId);
      if (previousPlayer) {
        return previousPlayer.score;
      }
      return studentAverageScores.get(studentId) ?? 0;
    },
    [previousGamePlayers, studentAverageScores],
  );

  // 지난 게임 불러오기 핸들러
  const handleLoadPreviousGame = useCallback(() => {
    if (!gameState.previousGameDate) {
      toast.error("날짜를 선택해주세요.");
      return;
    }

    if (!historyData || !historyData.leagues) {
      toast.error("지난 게임 데이터가 없습니다.");
      return;
    }

    // 기존 플레이어가 있으면 확인
    if (gameState.localPlayers.length > 0) {
      const confirmed = window.confirm(
        "현재 설정이 초기화됩니다. 계속하시겠습니까?"
      );
      if (!confirmed) return;
    }

    // 플레이어 목록 생성
    const players: LocalPlayer[] = [];
    historyData.leagues.forEach((league) => {
      league.players?.forEach((player) => {
        if (!player.user) return;
        players.push({
          id: player.user._id,
          name: player.user.name,
          score: player.score ?? 0,
          isSaved: false,
          isFromPreviousGame: true,
        });
      });
    });

    // 리그 설정 (이전 게임 리그 구조 유지)
    const leagues: LocalLeague[] = historyData.leagues.map((league) => ({
      name: league.name,
      playerCount: league.players?.length ?? 0,
      managedBy: null,
      matches: [],
      isFinished: false,
      isOvertime: false,
      overtimeMatches: [],
    }));

    // 상태 업데이트
    gameState.setLocalPlayers(players);
    gameState.setLocalLeagues(leagues);
    gameState.setConfirmedPreviousGameDate(gameState.previousGameDate);

    toast.success("지난 게임 데이터를 불러왔습니다.");
  }, [gameState, historyData]);

  // 플래시카드 배틀 시작 핸들러
  const handleStartBattle = useCallback(
    (leagueName: string, player1Id: string, player2Id: string, isOvertime: boolean) => {
      // 카테고리/페이지가 선택되지 않은 경우
      if (!categorySelection || categorySelection.pages.length === 0) {
        toast.error("단어장과 페이지를 먼저 선택해주세요.");
        return;
      }

      // 단어가 로드되지 않은 경우
      if (!battleWords || battleWords.length === 0) {
        toast.error("단어를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
        return;
      }

      // 플레이어 정보 찾기
      const player1 = gameState.localPlayers.find((p) => p.id === player1Id);
      const player2 = gameState.localPlayers.find((p) => p.id === player2Id);

      if (!player1 || !player2) {
        toast.error("플레이어 정보를 찾을 수 없습니다.");
        return;
      }

      // 단어 섞기
      const shuffledWords = [...battleWords].sort(() => Math.random() - 0.5);

      // 배틀 시작
      flashcardBattle.startBattle(
        { id: player1.id, name: player1.name },
        { id: player2.id, name: player2.name },
        leagueName,
        shuffledWords,
        isOvertime,
      );
    },
    [categorySelection, battleWords, gameState.localPlayers, flashcardBattle],
  );

  // 플래시카드 배틀 결과 선택 핸들러
  const handleBattleSelectWinner = useCallback(
    (winnerId: string) => {
      const state = flashcardBattle.battleState;
      if (!state) return;

      // 연장전 여부에 따라 적절한 핸들러 사용
      if (state.isOvertime) {
        socketHandlers.handleRecordOvertimeMatch(
          state.leagueName,
          state.player1.id,
          state.player2.id,
          winnerId,
        );
      } else {
        socketHandlers.handleRecordMatch(
          state.leagueName,
          state.player1.id,
          state.player2.id,
          winnerId,
        );
      }

      // 배틀 종료
      flashcardBattle.closeBattle();
    },
    [flashcardBattle, socketHandlers, gameActions],
  );

  // 에러 처리
  const errorStatus = (gameError as { response?: { status?: number } })
    ?.response?.status;

  // 404: 게임을 찾을 수 없음 → 목록으로 리다이렉트
  useEffect(() => {
    if (errorStatus === 404) {
      toast.error("게임을 찾을 수 없습니다.");
      navigate({ to: "/competition", replace: true });
    }
  }, [errorStatus, router]);

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">로딩 중...</div>
        </div>
      </PageContainer>
    );
  }

  // 403: 권한 없음
  if (errorStatus === 403) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="text-muted-foreground">
            이 게임에 접근할 권한이 없습니다.
          </div>
          <Button variant="outline" onClick={() => navigate({ to: "/competition" })}>
            목록으로 돌아가기
          </Button>
        </div>
      </PageContainer>
    );
  }

  // 기타 에러 (네트워크, 서버 에러 등)
  if (gameError && errorStatus !== 404) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="text-muted-foreground">
            게임을 불러오는 중 오류가 발생했습니다.
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refetch()}>
              다시 시도
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/competition" })}
            >
              목록으로
            </Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!game) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">게임을 찾을 수 없습니다.</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Competition</h1>
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            상태: <span className="font-medium">{game.status}</span>
            {(game.status !== "done" || isEditMode) && isConnected && (
              <span className="ml-2 text-green-500">● 연결됨</span>
            )}
          </div>
        </div>
      </div>

      {/* Ready 상태: 전체 설정 헤더 */}
      {game.status === "ready" && (
        <>
          <ValidationWarnings
            playerCountMismatch={gameState.playerCountMismatch}
            hasUnassignedPlayers={gameState.hasUnassignedPlayers}
            totalCapacity={gameState.totalCapacity}
            playerCount={gameState.localPlayers.length}
            promotionCountError={gameState.promotionCountError}
          />
          <GameHeader
            office={{ _id: game.office._id, name: game.office.name }}
            currentGroupId={game.group._id}
            groups={[{ _id: game.group._id, name: game.group.name }]}
            previousGameDate={gameState.previousGameDate}
            isDirty={gameState.isDirty}
            canSave={gameState.canSave}
            canStart={gameState.canStart}
            canDelete={isCreator}
            isSaving={socketHandlers.isSaving}
            isStarting={socketHandlers.isStarting}
            onGroupChange={() => {}}
            onPreviousGameDateChange={gameState.setPreviousGameDate}
            onLoadPreviousGame={handleLoadPreviousGame}
            onReset={gameActions.handleReset}
            onSave={socketHandlers.handleSave}
            onStartGame={socketHandlers.handleStartGame}
            onDeleteGame={socketHandlers.handleDeleteGame}
          />
          <div className="h-px bg-border mb-6" />
        </>
      )}

      {/* In Progress / Done 상태: 간단한 정보 표시 */}
      {(game.status === "in_progress" || game.status === "done") && (
        <div className="flex items-center gap-4 mb-6 text-sm text-muted-foreground">
          <span>{game.office.name}</span>
          <span>·</span>
          <span>{game.group.name}</span>
          <span>·</span>
          <span>
            {new Date(game.date).toLocaleDateString("ko-KR", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>
      )}

      {/* Ready State */}
      {game.status === "ready" && (
        <ReadyStateView
          currentUser={
            currentUser
              ? {
                  _id: currentUser._id,
                  name: currentUser.name,
                  userId: currentUser.userId,
                }
              : { _id: "", name: "", userId: "" }
          }
          localPromotionCount={gameState.localPromotionCount}
          localLeagues={gameState.localLeagues}
          playersWithLeague={gameState.playersWithLeague}
          pendingSelections={pendingSelections}
          availableStudents={availableStudents}
          currentGroupStudents={currentGroupStudents}
          onPromotionCountChange={gameState.setLocalPromotionCount}
          onLeagueCountChange={gameActions.handleLeagueCountChange}
          onToggleManage={socketHandlers.handleToggleManage}
          onAddLeague={gameActions.handleAddLeague}
          onRemoveLeague={gameActions.handleRemoveLeague}
          onAddPlayer={socketHandlers.handleAddPlayer}
          onRemovePlayer={gameActions.handleRemovePlayer}
          onPendingSelectionsChange={setPendingSelections}
          getPendingStudentLeague={getPendingStudentLeague}
          getPendingStudentScore={getPendingStudentScore}
          isIndependentMode={isIndependentMode}
        />
      )}

      {/* In Progress State */}
      {game.status === "in_progress" && (
        <>
          <InProgressStateView
            leagues={gameState.localLeagues}
            activeLeagueTab={gameState.activeLeagueTab}
            availableStudents={availableStudents}
            pendingLateJoin={lateJoin.pendingLateJoin}
            onTabChange={gameState.setActiveLeagueTab}
            onSelectLateJoinPlayer={lateJoin.handleSelectLateJoinPlayer}
            onChangeLateJoinLeague={lateJoin.handleChangeLateJoinLeague}
            onConfirmLateJoin={lateJoin.handleConfirmLateJoin}
            onCancelLateJoin={lateJoin.handleCancelLateJoin}
            getLeaguePlayers={gameState.getLeaguePlayers}
            getTiedPlayers={gameState.getTiedPlayers}
            isLeagueComplete={gameState.isLeagueComplete}
            needsOvertime={gameState.needsOvertime}
            isOvertimeComplete={gameState.isOvertimeComplete}
            onRecordMatch={socketHandlers.handleRecordMatch}
            onDeleteMatch={gameActions.handleDeleteMatch}
            onRecordOvertimeMatch={socketHandlers.handleRecordOvertimeMatch}
            onDeleteOvertimeMatch={gameActions.handleDeleteOvertimeMatch}
            onStartOvertime={gameActions.handleStartOvertime}
            onFinishLeague={socketHandlers.handleFinishLeague}
            finishingLeague={socketHandlers.finishingLeague ?? undefined}
            officeId={game.office._id}
            categorySelection={categorySelection}
            onCategorySelectionChange={setCategorySelection}
            onStartBattle={handleStartBattle}
          />

          {/* 플래시카드 배틀 오버레이 */}
          {flashcardBattle.battleState && (
            <FlashcardBattleOverlay
              battleState={flashcardBattle.battleState}
              onFlip={flashcardBattle.flipCard}
              onNext={flashcardBattle.nextWord}
              onShowResultSelector={flashcardBattle.showResultSelector}
              onHideResultSelector={flashcardBattle.hideResultSelector}
              onSelectWinner={handleBattleSelectWinner}
              onClose={flashcardBattle.closeBattle}
            />
          )}

          {/* 종료된 리그 결과 (부분 결과) */}
          {gameState.hasFinishedLeagues && (
            <ResultsTable
              results={gameState.finalResults}
              promotionCount={parseInt(gameState.localPromotionCount) || 0}
              isPartial={true}
              isIndependentMode={isIndependentMode}
            />
          )}
        </>
      )}

      {/* Done State */}
      {game.status === "done" && (
        <>
          {currentUser?.role === "admin" && (
            <div className="mb-4">
              <Button
                variant={isEditMode ? "default" : "outline"}
                onClick={() => setIsEditMode(!isEditMode)}
              >
                {isEditMode ? "편집 완료" : "결과 수정"}
              </Button>
            </div>
          )}

          {isEditMode && (
            <DoneEditView
              leagues={gameState.localLeagues}
              activeLeagueTab={gameState.activeLeagueTab}
              onTabChange={gameState.setActiveLeagueTab}
              getLeaguePlayers={gameState.getLeaguePlayers}
              getTiedPlayers={gameState.getTiedPlayers}
              onRecordMatch={socketHandlers.handleRecordMatch}
              onRecordOvertimeMatch={socketHandlers.handleRecordOvertimeMatch}
            />
          )}

          <ResultsTable
            results={gameState.finalResults}
            promotionCount={parseInt(gameState.localPromotionCount) || 0}
            isIndependentMode={isIndependentMode}
          />
        </>
      )}
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/competition/$gameId")({
  component: CompetitionGamePage,
});
